import React, {useEffect, useState} from "react";
import {Box, Button, TextField, Alert} from "@mui/material";
import {subscriptionServices} from "../../service/subscriptionService";
import Spinner from "../spinner/Spinner";
import {DialogActions, DialogContent} from "@mui/material";
import {AuthCard, AuthLayout} from "./AuthDesign";
import CustomSnackbar from "../alert/CustomSnackbar";
import {connect} from "react-redux";
import {useForm} from "react-hook-form";
import {countries} from "../utils/DataList";
import {BillingDetailsCountrySelect} from "../settings/subscription/UpdateBillingDetails";
import {authActions} from "../../store/actions/authAction";

function UpdateBillingData(props) {

    const {confirmBillingDetailsUpdate} = props;

    const {register, handleSubmit, control, setValue, formState: {errors}} = useForm();

    const [errorMsg, setErrorMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const handleOnSubmit = async (data) => {
        setLoading(true);
        setErrorMsg("");
        let dataObj = {
            name: data?.name,
            address1: data?.address1,
            address2: data?.address2,
            country: data?.country?.code,
            city: data?.city,
            state: data?.state,
            zip: data?.zip,
            vatNumber: data?.vatNumber,
        };

        await subscriptionServices.updateBillingDetails(dataObj).then(() => {
            confirmBillingDetailsUpdate();
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        if (props?.auth?.hasCompletedBillingDetailsUpdate) {
            document.location.replace("/");
        }
    }, [props?.auth?.hasCompletedBillingDetailsUpdate]);

    useEffect(() => {
        setLoading(true);
        subscriptionServices.getBillingDetails().then((result) => {
            setValue("name", result?.name);
            setValue("address1", result?.address1);
            setValue("address2", result?.address2);
            setValue("city", result?.city);
            setValue("state", result?.state);
            setValue("zip", result?.zip);
            setValue("vatNumber", result?.vatNumber);
            countries.forEach((item) => {
                if (item.code === result?.country && item.code !== "") {
                    setValue("country", {code: item.code, label: item.label});
                }
            });
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    }, [control, setValue]);

    return (
        <AuthLayout session>
            <AuthCard title="Update your billing details" description="Keep your company information and invoice details up to date.">
                {loading && <Spinner size={32} overComponentBox={true}/>}
                <DialogContent sx={{padding: "6px"}}>

                    <Alert severity="info" variant="filled" sx={{marginBottom: "20px"}}>
                        Please note that our company is now VAT-registered.
                        In accordance with EU VAT regulations, we require you to update your billing details:
                        <ul style={{paddingLeft: 16, margin: 0}}>
                            <li style={{paddingTop: 8}}>
                                If your business is established in the European Union, you must provide a valid VAT
                                identification number. If no valid VAT number is provided, we are legally required to
                                apply VAT to your monthly invoices.
                            </li>
                        </ul>
                    </Alert>

                    <form onSubmit={handleSubmit(handleOnSubmit)} autoComplete={"off"}>
                        {errorMsg !== "" &&
                            <CustomSnackbar message={errorMsg} timeout={2500} severity={"error"}/>
                        }
                        <Box className="auth-billing-fields">
                            <Box p={1} flexGrow={1}>
                                <TextField
                                    error={!!errors?.name}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    id="name"
                                    {...register("name", {required: true, maxLength: 100})}
                                    name="name"
                                    label="Name / Company Name *"
                                    placeholder="Enter your name or company name"
                                    type="text"
                                    fullWidth
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                            <Box p={1} flexGrow={1}>
                                <BillingDetailsCountrySelect control={control} register={register} errors={errors}/>
                            </Box>
                            <Box p={1} flexGrow={1}>
                                <TextField
                                    error={!!errors?.vatNumber}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    id="vatNumber"
                                    {...register("vatNumber", {maxLength: 100})}
                                    name="vatNumber"
                                    label="VAT Number (For EU Business customers)"
                                    placeholder="Enter your VAT number"
                                    type="text"
                                    fullWidth
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                            <Box p={1} flexGrow={1}>
                                <TextField
                                    error={!!errors?.address1}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    id="address1"
                                    {...register("address1", {maxLength: 100})}
                                    name="address1"
                                    label="Address 1"
                                    placeholder="Enter your address 1"
                                    type="text"
                                    fullWidth
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                            <Box p={1} flexGrow={1}>
                                <TextField
                                    error={!!errors?.address2}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    id="address2"
                                    {...register("address2", {maxLength: 100})}
                                    name="address2"
                                    label="Address 2"
                                    placeholder="Enter your address 2"
                                    type="text"
                                    fullWidth
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                            <Box p={1} flexGrow={1}>
                                <TextField
                                    error={!!errors?.city}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    id="city"
                                    {...register("city", {maxLength: 100})}
                                    name="city"
                                    label="City"
                                    placeholder="Enter your city"
                                    type="text"
                                    fullWidth
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                            <Box p={1} flexGrow={1}>
                                <TextField
                                    error={!!errors?.state}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    id="state"
                                    {...register("state", {maxLength: 100})}
                                    name="state"
                                    label="State"
                                    placeholder="Enter your state"
                                    type="text"
                                    fullWidth
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                            <Box p={1} flexGrow={1}>
                                <TextField
                                    error={!!errors?.zip}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    id="zip"
                                    {...register("zip", {maxLength: 100})}
                                    name="zip"
                                    label="Zip"
                                    placeholder="Enter your zip code"
                                    type="text"
                                    fullWidth
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </form>
                </DialogContent>
                <DialogActions sx={{padding: 2}}>
                    <Button fullWidth type="submit" color="primary" variant="contained" disabled={loading}
                            onClick={handleSubmit(handleOnSubmit)}>
                        Confirm & Update
                    </Button>
                </DialogActions>
            </AuthCard>
        </AuthLayout>
    );
}

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    };
};

const mapDispatchToProps = {
    confirmBillingDetailsUpdate: authActions.confirmBillingDetailsUpdate
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateBillingData);
